import { PropsWithChildren, useEffect, useState } from 'react';

import {
  Button,
  ChakraText,
  CircularProgress,
  EquipUITheme,
  Modal,
} from '@equip.health/ui';
import { useIdleTimer, workerTimers } from 'react-idle-timer';

import { TimerEvents } from '~/analytics/timerEvents';
import { MINUTE, SECOND } from '~/lib/constants';
import { VITE_INACTIVITY_TIMEOUT } from '~/lib/constants/env';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import useHandleLogout from '~/lib/hooks/useHandleLogout';

const TOP_LEVEL_ID = 'idle-modal';
const VITE_INACTIVITY_TIMEOUT_MS = VITE_INACTIVITY_TIMEOUT
  ? parseInt(VITE_INACTIVITY_TIMEOUT, 10) * MINUTE
  : 30 * MINUTE;
const IDLE_PROMPT_TIME_MS = 5 * MINUTE;

const { h4, label } = EquipUITheme.typography;

const IdleTimerContainer = ({ children }: PropsWithChildren) => {
  const [isPromptOpen, setIsPromptOpen] = useState<boolean>(false);
  const { trackEvent } = useAnalytics();
  const [promptTimeRemaining, setPromptTimeRemaining] =
    useState<number>(IDLE_PROMPT_TIME_MS);
  const { logout: handleLogout } = useHandleLogout();

  const onPrompt = () => {
    trackEvent(TimerEvents.idleTimerInitiated);
    setIsPromptOpen(true);
  };

  /* when the user clicks stay logged in on the prompt in one tab, this handler receives the event and ensures
   * all tabs close the prompt */
  const onActive = () => {
    setIsPromptOpen(false);
    setPromptTimeRemaining(IDLE_PROMPT_TIME_MS);
  };

  const { activate, getRemainingTime } = useIdleTimer({
    crossTab: true,
    onActive,
    onIdle: () => {
      setIsPromptOpen(false);
      handleLogout(false);
    },
    onPrompt,
    promptBeforeIdle: IDLE_PROMPT_TIME_MS,
    syncTimers: 1000,
    timeout: VITE_INACTIVITY_TIMEOUT_MS,
    timers: workerTimers,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPromptOpen) {
        setPromptTimeRemaining(Math.ceil(getRemainingTime()));
      }
    }, SECOND);
    return () => {
      clearInterval(interval);
    };
  }, [getRemainingTime, isPromptOpen]);

  const handleClosePrompt = () => {
    activate();
  };

  const timerRenderer = (): string => {
    const timeRemainingInSeconds = Math.ceil(promptTimeRemaining / SECOND);
    return `${Math.floor(timeRemainingInSeconds / 60)} : ${(
      timeRemainingInSeconds % 60
    )
      .toString()
      .padStart(2, '0')}`;
  };

  const footerContentRenderer = () => (
    <Button
      id={`${TOP_LEVEL_ID}__stay_logged_in`}
      onClick={handleClosePrompt}
      width="100%"
    >
      Stay Logged In
    </Button>
  );

  const progressLabelRenderer = () => (
    <>
      <ChakraText {...h4}>{timerRenderer()}</ChakraText>
      <ChakraText {...label}>Time left</ChakraText>
    </>
  );

  return (
    <>
      {children}
      <Modal
        bodyStyle={{ textAlign: 'center' }}
        footerContent={footerContentRenderer()}
        hideCloseButton
        id={TOP_LEVEL_ID}
        isOpen={isPromptOpen}
        onClose={handleClosePrompt}
        shouldCloseOnEscape={false}
        shouldCloseOnOverlayClick={false}
        size="sm"
        title="Are you still there?"
        titleStyle={{ margin: 'auto' }}
      >
        <CircularProgress
          capIsRound
          id="idle-body"
          label={progressLabelRenderer()}
          size="120px"
          thickness="7px"
          value={100 * (promptTimeRemaining / IDLE_PROMPT_TIME_MS)}
        />
        <ChakraText paddingTop={8} {...label}>
          Due to {VITE_INACTIVITY_TIMEOUT_MS / MINUTE} minutes of inactivity,
          for security reasons you will be automatically logged out.
        </ChakraText>
      </Modal>
    </>
  );
};

export default IdleTimerContainer;
